import { useEffect, useState } from 'react';
import {
  financialManagementAccountList,
  getCashOrderPayments,
  getDailyCashChange,
  getNonCashOrderPayments,
  getRefundOrderPayments,
  getTodayBeginningCashAmount,
} from 'apis/fms';
import type { NonCashOrderPayment } from 'apis/fms';
import { getCashPaymentSummary, getRecycleOrderDailyStats } from 'apis/oms';
import type {
  CashPaymentSummaryItem,
  OmsRecycleOrderDailyStat,
} from 'apis/oms';
import type { CashBreakdown, FmsAccountManagement } from 'types/fms';

export type CashSettlementDataPatch = Partial<{
  beginningCash: number;
  treasuryCashInAmount: number;
  treasuryCashOutAmount: number;
  actualCash: number;
  note: string;
  cashCounter: string;
  cashBreakdown: CashBreakdown;
}>;

type UseCashSettlementDataParams = {
  isUserLoading: boolean;
  selectedShopId?: number;
  username?: string;
  onCashDataChange: (patch: CashSettlementDataPatch) => void;
  onCashBreakdownVisibilityChange: (visible: boolean) => void;
};

export const useCashSettlementData = ({
  isUserLoading,
  selectedShopId,
  username,
  onCashDataChange,
  onCashBreakdownVisibilityChange,
}: UseCashSettlementDataParams) => {
  const [beginningCash, setBeginningCash] = useState(0);
  const [creditCardPayments, setCreditCardPayments] = useState<
    NonCashOrderPayment[]
  >([]);
  const [cashOrderPayments, setCashOrderPayments] = useState<
    NonCashOrderPayment[]
  >([]);
  const [paymentAccountList, setPaymentAccountList] = useState<
    FmsAccountManagement[]
  >([]);
  const [cashPaymentSummaryList, setCashPaymentSummaryList] = useState<
    CashPaymentSummaryItem[]
  >([]);
  const [recycleOrderDailyStats, setRecycleOrderDailyStats] = useState<
    OmsRecycleOrderDailyStat[]
  >([]);
  const [dailyCashChangeAmount, setDailyCashChangeAmount] = useState(0);
  const [refundOrderPayments, setRefundOrderPayments] = useState<
    NonCashOrderPayment[]
  >([]);

  useEffect(() => {
    if (isUserLoading || !selectedShopId) return;

    let cancelled = false;

    setBeginningCash(0);
    onCashDataChange({
      beginningCash: 0,
      treasuryCashInAmount: 0,
      treasuryCashOutAmount: 0,
      actualCash: 0,
      note: '',
      cashBreakdown: {
        bills: {},
        coins: {},
      },
    });
    onCashBreakdownVisibilityChange(false);
    setCreditCardPayments([]);
    setCashOrderPayments([]);
    setPaymentAccountList([]);
    setCashPaymentSummaryList([]);
    setRecycleOrderDailyStats([]);
    setDailyCashChangeAmount(0);
    setRefundOrderPayments([]);

    const initializeData = async () => {
      try {
        const [
          suggestedBeginningCash,
          creditCardData,
          cashOrderData,
          accountList,
          cashPaymentSummaryData,
          recycleOrderDailyStatsData,
          dailyCashChangeData,
          refundOrderData,
        ] = await Promise.all([
          getTodayBeginningCashAmount(selectedShopId),
          getNonCashOrderPayments(selectedShopId),
          getCashOrderPayments(selectedShopId),
          financialManagementAccountList(selectedShopId),
          getCashPaymentSummary(selectedShopId),
          getRecycleOrderDailyStats(selectedShopId),
          getDailyCashChange(selectedShopId),
          getRefundOrderPayments(selectedShopId),
        ]);

        const resolvedBeginningCash = suggestedBeginningCash ?? 0;

        if (cancelled) return;

        setBeginningCash(resolvedBeginningCash);
        onCashDataChange({
          beginningCash: resolvedBeginningCash,
          cashCounter: username || '',
        });
        setCreditCardPayments(creditCardData ?? []);
        setCashOrderPayments(cashOrderData ?? []);
        setPaymentAccountList(accountList ?? []);
        setCashPaymentSummaryList(cashPaymentSummaryData ?? []);
        setRecycleOrderDailyStats(recycleOrderDailyStatsData ?? []);
        setDailyCashChangeAmount(Number(dailyCashChangeData ?? 0));
        setRefundOrderPayments(refundOrderData ?? []);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to initialize data:', error);
        }
      }
    };

    initializeData();

    return () => {
      cancelled = true;
    };
  }, [
    isUserLoading,
    onCashBreakdownVisibilityChange,
    onCashDataChange,
    selectedShopId,
    username,
  ]);

  return {
    beginningCash,
    creditCardPayments,
    cashOrderPayments,
    paymentAccountList,
    cashPaymentSummaryList,
    recycleOrderDailyStats,
    dailyCashChangeAmount,
    refundOrderPayments,
  };
};
